import React, { ChangeEvent, ComponentProps, FormEvent, useEffect, useState } from 'react';
import { Button, Flex, Paper, Text, Link, Placeholder, IconButton, Icon } from '../../styles';
import Avatar from '../../styles/Avatar';
import ClickAwayListener from 'react-click-away-listener';
import { __t } from '../../i18n';
import { useUser } from 'common/CacheProvider';
import { useRouter } from 'next/router';
import Textarea from 'common/styles/Textarea';
import { IconSend } from '../../icons';
import styled from '@emotion/styled';

type CommentFormProps = {
  onSubmit: (comment: string) => void;
  state: {
    loading: boolean;
    success: boolean;
  };
};

export default function CommentForm({ onSubmit, state: { loading, success } }: CommentFormProps) {
  const [comment, setComment] = useState('');
  const [show, setShow] = useState(false);
  const { authorized, loaded, user } = useUser();

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setComment(value);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    comment && onSubmit(comment);
  };

  useEffect(() => {
    if (success) {
      setComment('');
      setShow(false);
    }
  }, [success]);

  if (!loaded) return <Loading />;
  if (!authorized) return <LoginPrompt />;

  return (
    <Paper p={3}>
      <form onSubmit={handleSubmit}>
        <Flex align="center" nowrap>
          <Avatar image={user?.avatar} size="2.5rem" />
          <ClickAwayListener onClickAway={() => setShow(false)}>
            <InputGroup>
              <Textarea
                placeholder={__t('comments.form.enter_comment')}
                scale={2}
                value={comment}
                onFocus={() => setShow(true)}
                onChange={handleChange}
                expand={show}
              />
              <SubmitButton disabled={loading || !comment} />
            </InputGroup>
          </ClickAwayListener>
        </Flex>
      </form>
    </Paper>
  );
}

const SubmitButton = ({ disabled }: Pick<ComponentProps<typeof IconButton>, 'disabled'>) => (
  <CustomButton round type="submit" disabled={disabled} p={1}>
    <Icon cursor="pointer">
      <IconSend currentColor='#fff'/>
    </Icon>
  </CustomButton>
);

const CustomButton = styled(IconButton)(({ theme }) => {
  const [bg, fg] = theme.palette.success[900];

  return {
    color: fg,
    backgroundColor: bg,
    '&:hover, &:disabled': {
      color: fg,
      backgroundColor: theme.rgba(bg, 0.34),
    },
    '&:disabled': {
      pointerEvents: 'none',
    },
  };
});

const InputGroup = styled.div(({ theme }) => {
  return {
    position: 'relative',
    flexGrow: 1,
    marginRight: theme.spacing(2),
    '& textarea': {
      fontSize: theme.font.size(1),
      width: '100%',
      padding: '.75rem',
      paddingLeft: theme.spacing(6),
      display: 'block',
      margin: 0,
    },
    '& button': {
      position: 'absolute',
      left: theme.spacing(1),
      bottom: theme.spacing(1),
    },
  };
});

const Loading = () => (
  <Paper p={3}>
    <Placeholder height={4} fullWidth />
  </Paper>
);

const LoginPrompt = () => {
  const router = useRouter();
  return (
    <Paper p={3}>
      <Flex nowrap justify="space-between" align="center" direction={{ xs: 'column', sm: 'row' }}>
        <Flex nowrap align="center">
          <Avatar size="4rem" />
          <Text variant="body1" mr={2}>
            {__t('comments.form.login_for_comment')}
          </Text>
        </Flex>
        <Button variant="primaryA" as={Link} href={`/login?redirect=${router.asPath}`}>
          {__t('comments.form.login_and_comment')}
        </Button>
      </Flex>
    </Paper>
  );
};
